import { FC, ReactNode, useMemo, useState } from "react";

import DateTimeField from "@src/lib/ui/DateTimeField";
import { Label } from "@src/lib/ui/Label";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { RadioGroup, RadioGroupItem } from "@src/lib/ui/RadioGroup";
import { Separator } from "@src/lib/ui/Separator";
import ChevronsOpenIcon from "@src/lib/icons/12px/chevrons-open";
import {
  formatDateTime,
  getNowForDatetimeLocalHtmlInput,
} from "@src/lib/utils/dates";
import { cn } from "@src/lib/utils/cn";
import { inputFieldClassName } from "@src/lib/ui/Input";
import { getBrowserTimezone } from "@src/lib/utils/time";

type Props = {
  scheduledDateTime?: Date | null | undefined;
  onScheduledDateTimeChange: (val: Date | null | undefined) => void;
  className?: string;
  noDateSelectedLabel?: string;
  messageSchedulingEnabled?: boolean;
  fullWidth?: boolean;
  /**
   * Where the popover opens relative to the trigger button. Defaults to
   * "top" to preserve existing callers (automations, message blasts) which
   * sit near the bottom of their dialogs.
   */
  menuPlacement?: "top" | "bottom";
  /**
   * Optional icon rendered at the start of the trigger button. Callers like
   * Message Blasts pass a clock icon; callers like Scheduled Invites (which
   * live next to a Select-styled UserType dropdown) omit it.
   */
  leadingIcon?: ReactNode;
  /**
   * Optional description text shown beneath the default (no-date-selected)
   * radio option. Omit to hide the line entirely. Pairs with
   * `noDateSelectedLabel` — e.g. "Now" + "Fire immediately." or "Never" +
   * undefined.
   */
  noDateSelectedDescription?: string;
  /**
   * Seed used when switching to "At a later date and time". Defaults to
   * now.
   */
  defaultScheduledDateTime?: Date;
  /** Fires when the popover opens/closes. */
  onOpenChange?: (open: boolean) => void;
};

const NowOrScheduledPicker: FC<Props> = ({
  scheduledDateTime,
  onScheduledDateTimeChange,
  className,
  noDateSelectedLabel = "Now",
  messageSchedulingEnabled = false,
  fullWidth = false,
  menuPlacement = "top",
  leadingIcon,
  noDateSelectedDescription,
  defaultScheduledDateTime,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const minDateTime = useMemo(() => getNowForDatetimeLocalHtmlInput(), []);
  const timezone = getBrowserTimezone();

  const scheduledDateTimeAsDate = useMemo(
    () => (scheduledDateTime ? new Date(scheduledDateTime) : scheduledDateTime),
    [scheduledDateTime],
  );
  const isScheduled = !!scheduledDateTimeAsDate;

  const buttonLabel = isScheduled
    ? `${messageSchedulingEnabled ? "Send on " : ""}${formatDateTime(
        scheduledDateTimeAsDate,
      )} ${timezone}`
    : messageSchedulingEnabled
      ? `Send ${noDateSelectedLabel.toLocaleLowerCase()}`
      : noDateSelectedLabel;

  return (
    // The wrapping div is intentional: the Button rendered by `PopoverTrigger
    // asChild` is `inline-flex`, which layouts inline alongside a sibling
    // <Label> when the picker is used inside a typical form field. Wrapping
    // in a block-level div ensures label-above-picker stacking (matching the
    // shadcn Select pattern).
    <div className={cn("block", fullWidth && "self-stretch")}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          onOpenChange?.(next);
        }}
      >
        <PopoverTrigger asChild>
          {/* The Input field look, so this picker pairs with an Input or
            Select in the same form row. */}
          <button
            type="button"
            className={cn(
              inputFieldClassName,
              "items-center justify-between gap-2 whitespace-nowrap py-2 text-left",
              // `w-auto` lets the trigger grow to fit the formatted datetime +
              // timezone without truncating; `min-w-[200px]` keeps the "Now"
              // state from looking oddly small next to the User type Select.
              fullWidth ? "w-full" : "w-auto min-w-[200px]",
              className,
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              {leadingIcon && (
                <span className="flex shrink-0 text-icon-secondary">
                  {leadingIcon}
                </span>
              )}
              <span className="truncate">{buttonLabel}</span>
            </div>
            <ChevronsOpenIcon className="shrink-0 text-icon-secondary" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side={menuPlacement}
          align="start"
          className="w-[280px] p-0"
        >
          <RadioGroup
            value={isScheduled ? "scheduled" : "now"}
            onValueChange={(val) => {
              if (val === "now") onScheduledDateTimeChange(undefined);
              else
                onScheduledDateTimeChange(
                  defaultScheduledDateTime ?? new Date(),
                );
            }}
            className="gap-0"
          >
            <label
              htmlFor="now-picker-now"
              className={cn(
                "flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-state-hover",
                !isScheduled && "bg-bg-secondary",
              )}
            >
              <RadioGroupItem value="now" id="now-picker-now" />
              <div className="flex-1">
                <Label htmlFor="now-picker-now" className="cursor-pointer">
                  {noDateSelectedLabel}
                </Label>
                {noDateSelectedDescription && (
                  <p className="text-2xs-v4 text-text-tertiary">
                    {noDateSelectedDescription}
                  </p>
                )}
              </div>
            </label>
            <Separator />
            <label
              htmlFor="now-picker-scheduled"
              className={cn(
                "flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors hover:bg-state-hover",
                isScheduled && "bg-bg-secondary",
              )}
            >
              <RadioGroupItem
                value="scheduled"
                id="now-picker-scheduled"
                className="mt-0.5"
              />
              <div className="flex-1 space-y-2">
                <div>
                  <Label
                    htmlFor="now-picker-scheduled"
                    className="cursor-pointer"
                  >
                    At a later date and time
                  </Label>
                  <p className="text-2xs-v4 text-text-tertiary">
                    Times are in {timezone}.
                  </p>
                </div>
                <DateTimeField
                  value={scheduledDateTimeAsDate}
                  onChange={onScheduledDateTimeChange}
                  min={minDateTime}
                  className={cn(
                    "transition-opacity",
                    !isScheduled && "pointer-events-none opacity-40",
                  )}
                />
              </div>
            </label>
          </RadioGroup>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default NowOrScheduledPicker;
